/**
 * Raport zarządu — teksty sekcji finance (`mbr.finance.*`). WŁAŚCICIEL: agent sekcji finance (docs/RAPORT-ZARZADU.md).
 * Podpięte już w i18n/mbr.ts — dopisuj klucze tutaj, nie w extra.ts. `en` typowane względem `pl`.
 * Własne kody uzgodnień sekcji: `checks: { KOD: '…' }` (panel szuka `mbr.checks.KOD`, potem `mbr.finance.checks.KOD`).
 */
import { mbrFinanceUiEn, mbrFinanceUiPl } from './mbr-finance-ui.ts';
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrFinancePl = {
  title: 'Koszty i należności',
  ui: mbrFinanceUiPl,
  description: 'Koszty stałe i zmienne, wynagrodzenia, należności z wiekowaniem, windykacja, kadry.',
  tabs: { costs: 'Koszty', receivables: 'Należności', collections: 'Windykacja', hr: 'Kadry' },
  kpi: {
    fixedCosts: 'Koszty stałe bez wynagrodzeń',
    variableCosts: 'Koszty zmienne bez wynagrodzeń',
    payroll: 'Wynagrodzenia',
    payrollPerSalesPln: 'Wynagrodzenia na 1 zł sprzedaży',
    receivablesOverdue: 'Należności przeterminowane',
    collectionsAmount: 'Windykacja — kwota w kancelarii',
    totalCosts: 'Koszty razem',
    headcount: 'Liczba osób',
    employerCost: 'Koszt pracodawcy (kadry)',
    erpOverdue: 'Przeterminowane w Subiekcie',
    erpCei: 'Skuteczność windykacji (CEI)',
    pctOfSales: '{pct}% sprzedaży',
  },
  cost: {
    title: 'Koszty wg kategorii i oddziału',
    subtitle: 'Kolumny: okres bieżący, bazowy, zmiana. Rozwiń kategorię, żeby zobaczyć oddziały.',
    kindsTitle: 'Struktura kosztów',
    trendTitle: 'Koszty i sprzedaż — 12 miesięcy',
    trendSubtitle: 'Słupki: koszty stałe, zmienne, wynagrodzenia; linia: sprzedaż po korektach.',
    category: 'Kategoria',
    pctOfSales: '% sprzedaży',
    pctOfSalesBase: '% sprzedaży (bazowy)',
    sales: 'Sprzedaż po korektach',
    total: 'Razem',
    company: 'Cała firma',
    empty: 'Brak kosztów w tym okresie — uzupełnij formularz „Koszty”.',
    semiNote: 'Koszty półzmienne (energia) liczą się do kosztów stałych — tak jak w progu rentowności.',
    kinds: {
      FIXED: 'Stałe bez wynagrodzeń',
      SEMI: 'Półzmienne',
      VARIABLE: 'Zmienne bez wynagrodzeń',
      PAYROLL: 'Wynagrodzenia',
    },
    categories: {
      fixedOther: 'Pozostałe koszty stałe',
      vehiclesRent: 'Pojazdy — najem',
      premises: 'Powierzchnie',
      equipmentLeasing: 'Leasing urządzeń',
      energy: 'Energia',
      variableOther: 'Pozostałe koszty zmienne',
      vehiclesOperation: 'Pojazdy — eksploatacja',
      packaging: 'Opakowania',
      transportPurchase: 'Usługi transportowe — zakup',
      transportB2b: 'Usługi transportowe B2B',
      payroll: 'Wynagrodzenia',
    },
  },
  receivables: {
    FACTORING: 'Należności — faktoring',
    OTHER: 'Należności — pozostałe',
    balance: 'Stan',
    overdue: 'Przeterminowane',
    overduePct: 'Udział przeterminowanych',
    notDue: 'Niewymagalne',
    upTo30: 'Do 30 dni',
    over30: 'Powyżej 30 dni',
    weekAgo: '7 dni wstecz',
    weekAgoOverdue: 'Przeterminowane 7 dni wstecz',
    change7d: 'Zmiana w 7 dni',
    repayments7d: 'Spłaty w 7 dni',
    asOf: 'Stan na {date}',
    noSnapshot: 'Brak migawki w tym okresie',
    weeklyTitle: 'Trend tygodniowy',
    weeklySubtitle: 'Stan i przeterminowane z migawek tygodniowych.',
    erpTitle: 'Należności z Subiekta',
    erpSubtitle: 'Z historii faktur (to samo źródło co BI → Należności), stan na {date}.',
    open: 'Otwarte należności',
    cei: 'Skuteczność windykacji (90 dni)',
    dso: 'Rotacja (DSO, dni)',
    agingTitle: 'Wiekowanie',
    monthlyTitle: 'Stan na koniec miesiąca',
    empty: 'Brak migawek należności — uzupełnij formularz „Należności”.',
    aging: {
      notDue: 'Niewymagalne',
      d0_30: '1–30 dni',
      d31_60: '31–60 dni',
      d61_90: '61–90 dni',
      d90plus: '>90 dni',
    },
  },
  collections: {
    title: 'Sprawy w kancelarii wg etapu',
    subtitle: 'Stan na koniec okresu: liczba spraw i kwota, zmiana względem okresu bazowego.',
    stage: 'Etap',
    cases: 'Sprawy',
    amount: 'Kwota',
    total: 'Razem',
    empty: 'Brak danych windykacji — uzupełnij formularz „Windykacja”.',
    stages: {
      reminderUpcoming: 'Przypomnienie przed terminem',
      reminderDue: 'Przypomnienie w dniu płatności',
      reminderOverdue: 'Przypomnienie o zaległości',
      dunningNotice: 'Monit',
      urge: 'Ponaglenie',
      demand: 'Wezwanie do zapłaty (e-mail)',
      demandLate: 'Zaległość powyżej 15 dni',
      preCourt: 'Przedsądowe',
      court: 'Sądowe',
      enforcement: 'Egzekucja komornicza',
      bankruptcy: 'Upadłość / restrukturyzacja',
      settlement: 'Ugoda',
    },
  },
  hr: {
    title: 'Kadry wg działów',
    subtitle: 'Sumy działów dla zarządu. Dane pojedynczych osób — tylko z uprawnieniem do kadr.',
    department: 'Dział',
    headcount: 'Osoby',
    employerCost: 'Koszt pracodawcy',
    perHead: 'Na osobę',
    perHour: 'Na godzinę',
    bonuses: 'Premie',
    hours: 'Godziny',
    total: 'Razem',
    payrollPerSales: 'Koszt wynagrodzeń na 1 zł sprzedaży: {value}',
    people: 'Lista osób',
    peopleTitle: 'Kadry — dane osobowe ({period})',
    peopleHint: 'Dane osobowe. Nie kopiuj ich poza system; dostęp tylko dla zarządu i kadr.',
    fullName: 'Imię i nazwisko',
    ref: 'Nr kadrowy',
    contract: 'Umowa',
    gross: 'Brutto',
    net: 'Netto',
    cost: 'Koszt',
    empty: 'Brak danych kadrowych — uzupełnij formularz „Kadry”.',
    peopleEmpty: 'Brak danych osób w tym okresie.',
  },
  edit: {
    button: 'Wprowadź dane',
    title: 'Dane finansowe — {period}',
    description: 'Puste pole = brak danych (nie zero). Wiersz bez wartości zostaje usunięty przy zapisie.',
    locked: 'Miesiąc jest zamknięty — dane tylko do odczytu.',
    noAccess: 'Nie jesteś osobą odpowiedzialną za ten formularz.',
    save: 'Zapisz',
    saved: 'Zapisano: {saved}, usunięto: {removed}.',
    addRow: 'Dodaj wiersz',
    remove: 'Wyczyść wiersz',
    branch: 'Oddział',
    amount: 'Kwota',
    label: 'Opis',
    date: 'Data migawki',
    kind: 'Rodzaj',
    invalid: 'Popraw oznaczone pola.',
    error: 'Nie udało się zapisać.',
    forms: { costs: 'Koszty', hr: 'Kadry', receivables: 'Należności', collections: 'Windykacja' },
  },
  checks: {
    FINANCE_COSTS_UNCHANGED: 'Koszty identyczne jak w {previous} co do grosza — dane nieodświeżone?',
    FINANCE_HR_UNCHANGED: 'Kadry identyczne jak w {previous} co do grosza — dane nieodświeżone?',
    FINANCE_RECEIVABLES_UNCHANGED:
      'Należności identyczne jak w {previous} co do grosza — migawka nieodświeżona?',
    FINANCE_COLLECTIONS_UNCHANGED:
      'Windykacja identyczna jak w {previous} co do grosza — dane nieodświeżone?',
    FINANCE_HR_EQUALS_PAYROLL:
      'Suma kosztu pracodawcy w kadrach ({actual}) różni się od wynagrodzeń w kosztach ({expected}) o {diff}.',
    FINANCE_PERIOD_YEAR: 'Brak danych za {period}, a są za {found} ({rows} wierszy) — sprawdź rok w danych.',
  },
  checkContext: {
    FACTORING: 'faktoring',
    OTHER: 'pozostałe',
    period: 'okres {period}',
    department: 'dział {department}',
    employees: '{employees} osób',
  },
};

export const mbrFinanceEn: Shape<typeof mbrFinancePl> = {
  title: 'Costs & receivables',
  ui: mbrFinanceUiEn,
  description: 'Fixed and variable costs, payroll, receivables with ageing, collections, HR.',
  tabs: { costs: 'Costs', receivables: 'Receivables', collections: 'Collections', hr: 'HR' },
  kpi: {
    fixedCosts: 'Fixed costs excl. payroll',
    variableCosts: 'Variable costs excl. payroll',
    payroll: 'Payroll',
    payrollPerSalesPln: 'Payroll per 1 PLN of sales',
    receivablesOverdue: 'Overdue receivables',
    collectionsAmount: 'Collections — amount at the law firm',
    totalCosts: 'Total costs',
    headcount: 'Headcount',
    employerCost: 'Employer cost (HR)',
    erpOverdue: 'Overdue in Subiekt',
    erpCei: 'Collection effectiveness (CEI)',
    pctOfSales: '{pct}% of sales',
  },
  cost: {
    title: 'Costs by category and branch',
    subtitle: 'Columns: current period, base, change. Expand a category to see branches.',
    kindsTitle: 'Cost structure',
    trendTitle: 'Costs and sales — 12 months',
    trendSubtitle: 'Bars: fixed, variable, payroll; line: net sales after corrections.',
    category: 'Category',
    pctOfSales: '% of sales',
    pctOfSalesBase: '% of sales (base)',
    sales: 'Net sales after corrections',
    total: 'Total',
    company: 'Whole company',
    empty: 'No costs in this period — fill in the “Costs” form.',
    semiNote: 'Semi-variable costs (energy) count as fixed — as in the break-even point.',
    kinds: {
      FIXED: 'Fixed excl. payroll',
      SEMI: 'Semi-variable',
      VARIABLE: 'Variable excl. payroll',
      PAYROLL: 'Payroll',
    },
    categories: {
      fixedOther: 'Other fixed costs',
      vehiclesRent: 'Vehicles — rent',
      premises: 'Premises',
      equipmentLeasing: 'Equipment leasing',
      energy: 'Energy',
      variableOther: 'Other variable costs',
      vehiclesOperation: 'Vehicles — operation',
      packaging: 'Packaging',
      transportPurchase: 'Transport services — purchased',
      transportB2b: 'Transport services B2B',
      payroll: 'Payroll',
    },
  },
  receivables: {
    FACTORING: 'Receivables — factoring',
    OTHER: 'Receivables — other',
    balance: 'Balance',
    overdue: 'Overdue',
    overduePct: 'Overdue share',
    notDue: 'Not due',
    upTo30: 'Up to 30 days',
    over30: 'Over 30 days',
    weekAgo: '7 days back',
    weekAgoOverdue: 'Overdue 7 days back',
    change7d: 'Change in 7 days',
    repayments7d: 'Repayments in 7 days',
    asOf: 'As of {date}',
    noSnapshot: 'No snapshot in this period',
    weeklyTitle: 'Weekly trend',
    weeklySubtitle: 'Balance and overdue from weekly snapshots.',
    erpTitle: 'Receivables from Subiekt',
    erpSubtitle: 'From the invoice history (same source as BI → Receivables), as of {date}.',
    open: 'Open receivables',
    cei: 'Collection effectiveness (90 days)',
    dso: 'Days sales outstanding',
    agingTitle: 'Ageing',
    monthlyTitle: 'Month-end balance',
    empty: 'No receivable snapshots — fill in the “Receivables” form.',
    aging: {
      notDue: 'Not due',
      d0_30: '1–30 days',
      d31_60: '31–60 days',
      d61_90: '61–90 days',
      d90plus: '>90 days',
    },
  },
  collections: {
    title: 'Cases at the law firm by stage',
    subtitle: 'Month-end state: number of cases and amount, change vs the base period.',
    stage: 'Stage',
    cases: 'Cases',
    amount: 'Amount',
    total: 'Total',
    empty: 'No collections data — fill in the “Collections” form.',
    stages: {
      reminderUpcoming: 'Reminder before due date',
      reminderDue: 'Reminder on due date',
      reminderOverdue: 'Overdue reminder',
      dunningNotice: 'Dunning notice',
      urge: 'Urgent reminder',
      demand: 'Demand for payment (e-mail)',
      demandLate: 'Overdue more than 15 days',
      preCourt: 'Pre-court',
      court: 'Court',
      enforcement: 'Bailiff enforcement',
      bankruptcy: 'Bankruptcy / restructuring',
      settlement: 'Settlement',
    },
  },
  hr: {
    title: 'HR by department',
    subtitle: 'Department totals for the board. Individual people — only with the HR permission.',
    department: 'Department',
    headcount: 'People',
    employerCost: 'Employer cost',
    perHead: 'Per person',
    perHour: 'Per hour',
    bonuses: 'Bonuses',
    hours: 'Hours',
    total: 'Total',
    payrollPerSales: 'Payroll cost per 1 PLN of sales: {value}',
    people: 'People',
    peopleTitle: 'HR — personal data ({period})',
    peopleHint: 'Personal data. Do not copy it outside the system; board and HR only.',
    fullName: 'Full name',
    ref: 'Employee no.',
    contract: 'Contract',
    gross: 'Gross',
    net: 'Net',
    cost: 'Cost',
    empty: 'No HR data — fill in the “HR” form.',
    peopleEmpty: 'No people in this period.',
  },
  edit: {
    button: 'Enter data',
    title: 'Finance data — {period}',
    description: 'Empty field = no data (not zero). A row without values is removed on save.',
    locked: 'The month is closed — data is read-only.',
    noAccess: 'You are not the person responsible for this form.',
    save: 'Save',
    saved: 'Saved: {saved}, removed: {removed}.',
    addRow: 'Add row',
    remove: 'Clear row',
    branch: 'Branch',
    amount: 'Amount',
    label: 'Note',
    date: 'Snapshot date',
    kind: 'Kind',
    invalid: 'Fix the marked fields.',
    error: 'Could not save.',
    forms: { costs: 'Costs', hr: 'HR', receivables: 'Receivables', collections: 'Collections' },
  },
  checks: {
    FINANCE_COSTS_UNCHANGED: 'Costs identical to {previous} to the grosz — data not refreshed?',
    FINANCE_HR_UNCHANGED: 'HR identical to {previous} to the grosz — data not refreshed?',
    FINANCE_RECEIVABLES_UNCHANGED:
      'Receivables identical to {previous} to the grosz — snapshot not refreshed?',
    FINANCE_COLLECTIONS_UNCHANGED: 'Collections identical to {previous} to the grosz — data not refreshed?',
    FINANCE_HR_EQUALS_PAYROLL:
      'HR employer cost total ({actual}) differs from payroll in costs ({expected}) by {diff}.',
    FINANCE_PERIOD_YEAR: 'No data for {period}, but {found} has {rows} rows — check the year in the data.',
  },
  checkContext: {
    FACTORING: 'factoring',
    OTHER: 'other',
    period: 'period {period}',
    department: 'department {department}',
    employees: '{employees} people',
  },
};
